import React, { useState, useEffect } from "react";
import { useQuery, useLazyQuery, useMutation } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import moment from "moment";
import XLSX from "xlsx";
import IconButton from "@material-ui/core/IconButton";

import StarIcon from "@material-ui/icons/Star";
import ThumbDownIcon from "@material-ui/icons/ThumbDown";
import ChatBubbleOutlineIcon from "@material-ui/icons/ChatBubbleOutline";
import RefreshIcon from "@material-ui/icons/Refresh";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import DeleteIcon from "@material-ui/icons/Delete";

import ErrorPage from "commonComponents/errorPage/ErrorPage";
import CommentDetails from "./CommentDetails";
import DialogShell from "designV2/components/DialogShell";
import PageHeader from "designV2/components/PageHeader";
import PageActionButton from "designV2/components/PageActionButton";
import MetricCard from "designV2/components/MetricCard";
import SearchField from "designV2/components/SearchField";
import FilterDropdown from "designV2/components/FilterDropdown";
import DateRangeFilter from "designV2/components/DateRangeFilter";
import ActiveFilterChipList from "designV2/components/ActiveFilterChipList";
import EmptyState from "designV2/components/EmptyState";
import PaginationComponent from "designV2/components/Pagination";
import { MetricsSkeleton, TableSkeleton } from "designV2/components/Skeleton";
import { Stars, readableError } from "../detailFields";
import "../superRide.css";

/* ------------------------------------------------------------------ *
 * GraphQL
 *
 * The two review queues are the same screen against two resolvers, so
 * the documents are built from one field set. The pair of ~870-line
 * files this replaces differed only in the resolver names and in which
 * of userComment / driverComment holds the free text.
 * ------------------------------------------------------------------ */

const COMMENT_FIELDS = (commentField) => `
  _id
  createdAt
  ${commentField}
  readyComments {
    rate
    readyComment {
      _id
      type
    }
  }
  trip {
    _id
    tripType
    cost
  }
  driver {
    _id
    fullName
    phoneNumber
  }
  passenger {
    _id
    fullName
    phoneNumber
  }
`;

const COMMENT_FILTERS = `
  _id: $_id
  trip: $trip
  driverName: $driverName
  driverPhoneNumber: $driverPhoneNumber
  passengerName: $passengerName
  passengerPhoneNumber: $passengerPhoneNumber
  createdAtFrom: $createdAtFrom
  createdAt: $createdAt
`;

const COMMENT_VARIABLES = `
  $limit: Int
  $skip: Int
  $_id: ID
  $trip: ID
  $driverName: String
  $driverPhoneNumber: String
  $passengerName: String
  $passengerPhoneNumber: String
  $createdAtFrom: Date
  $createdAt: Date
  $createdAtSort: Int
`;

const buildQuery = (root, commentField) => gql`
  query ${root}(${COMMENT_VARIABLES}) {
    ${root}(
      pagination: { limit: $limit, skip: $skip }
      filters: { ${COMMENT_FILTERS} }
      sort: { createdAt: $createdAtSort }
    ) {
      ${COMMENT_FIELDS(commentField)}
    }
    ${root}Count(filters: { ${COMMENT_FILTERS} })
  }
`;

/**
 * Both remove mutations return MessageResponse, which has a `message`
 * and nothing else — but both screens asked for `_id`, so the request
 * failed validation on the server and deleting a review never worked on
 * either tab.
 */
const buildDelete = (name) => gql`
  mutation ${name}($id: ID!) {
    ${name}(id: $id) {
      message
    }
  }
`;

const TARGETS = {
  driver: {
    root: "getCommentsOnDriverByAdmin",
    commentField: "userComment",
    deleteName: "removeCommentOnDriverByAdmin",
    title: "Driver reviews",
    description: "What passengers said about their drivers after a trip.",
    subjectLabel: "Driver",
    authorLabel: "Passenger",
    subjectKey: "driver",
    authorKey: "passenger",
    emptyTitle: "No driver reviews yet",
  },
  passenger: {
    root: "getCommentsOnPassengerByAdmin",
    commentField: "driverComment",
    deleteName: "removeCommentOnPassengerByAdmin",
    title: "Passenger reviews",
    description: "What drivers said about their passengers after a trip.",
    subjectLabel: "Passenger",
    authorLabel: "Driver",
    subjectKey: "passenger",
    authorKey: "driver",
    emptyTitle: "No passenger reviews yet",
  },
};

const SEARCH_TARGETS = [
  { value: "driver", label: "Driver" },
  { value: "passenger", label: "Passenger" },
];
const SEARCH_LABELS = { driver: "Driver", passenger: "Passenger" };

const RATING_OPTIONS = [
  { value: "", label: "Any rating" },
  { value: "low", label: "1-2 stars" },
  { value: "mid", label: "3 stars" },
  { value: "high", label: "4-5 stars" },
];
const RATING_LABELS = RATING_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

const SORT_OPTIONS = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
];

const FILTER_DEBOUNCE_MS = 350;

const looksLikePhone = (value) => /^[\d\s+()-]+$/.test(value);
const formatDate = (value) => (value ? moment(value).format("MMM D, YYYY") : "");
const formatTime = (value) => (value ? moment(value).format("hh:mm A") : "");

// A review carries one rating per ready-comment tag; the score is their
// mean, and a review with no tags has no score at all.
const averageRate = (comment) => {
  const rates = (comment.readyComments || [])
    .map((r) => r.rate)
    .filter((r) => typeof r === "number");
  if (!rates.length) return null;
  return rates.reduce((sum, r) => sum + r, 0) / rates.length;
};

/* ------------------------------------------------------------------ *
 * Component
 * ------------------------------------------------------------------ */

const CommentsList = ({ target }) => {
  const config = TARGETS[target];
  const [query] = useState(() => buildQuery(config.root, config.commentField));
  const [deleteMutation] = useState(() => buildDelete(config.deleteName));

  const [searchTarget, setSearchTarget] = useState(target === "driver" ? "driver" : "passenger");
  const [searchTerm, setSearchTerm] = useState("");
  const [ratingFilter, setRatingFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortOption, setSortOption] = useState("newest");

  const [pageCount, setPageCount] = useState(0);
  const [perPage, setPerPage] = useState(10);

  const [commentDetail, setCommentDetail] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [actionError, setActionError] = useState("");
  const [exporting, setExporting] = useState(false);

  const [variables, setVariables] = useState({ limit: perPage, skip: 0, createdAtSort: -1 });

  useEffect(() => {
    const handler = setTimeout(() => {
      const term = searchTerm.trim();
      const search = {};
      if (term) {
        if (looksLikePhone(term)) search[`${searchTarget}PhoneNumber`] = term;
        else search[`${searchTarget}Name`] = term;
      }
      setVariables((v) => ({
        ...v,
        skip: 0,
        driverName: undefined,
        driverPhoneNumber: undefined,
        passengerName: undefined,
        passengerPhoneNumber: undefined,
        ...search,
        createdAtFrom: dateFrom || undefined,
        createdAt: dateTo || undefined,
        createdAtSort: sortOption === "oldest" ? 1 : -1,
      }));
      setPageCount(0);
    }, FILTER_DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [searchTarget, searchTerm, dateFrom, dateTo, sortOption]);

  useEffect(() => {
    setVariables((v) => ({ ...v, limit: perPage, skip: perPage * pageCount }));
  }, [perPage, pageCount]);

  const { data, loading, error, refetch } = useQuery(query, {
    variables,
    fetchPolicy: "network-only",
  });
  const [fetchAllForExport, { data: exportData }] = useLazyQuery(query, {
    fetchPolicy: "network-only",
  });
  const [removeComment] = useMutation(deleteMutation);

  const allComments = (data && data[config.root]) || [];
  const serverCount = (data && data[`${config.root}Count`]) || 0;

  // Rating is not a server-side filter, so this one narrows the page in
  // the browser and says so rather than pretending the count changed.
  const comments = allComments.filter((comment) => {
    if (!ratingFilter) return true;
    const rate = averageRate(comment);
    if (rate === null) return false;
    if (ratingFilter === "low") return rate < 3;
    if (ratingFilter === "mid") return rate >= 3 && rate < 4;
    return rate >= 4;
  });

  const ratedOnPage = allComments.map(averageRate).filter((r) => r !== null);
  const pageAverage = ratedOnPage.length
    ? ratedOnPage.reduce((sum, r) => sum + r, 0) / ratedOnPage.length
    : null;
  const lowOnPage = ratedOnPage.filter((r) => r < 3).length;

  /* ---------------- delete ---------------- */

  const runDelete = () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setActionError("");
    removeComment({ variables: { id: deleteTarget._id } })
      .then(() => {
        setDeleting(false);
        setDeleteTarget(null);
        setCommentDetail(null);
        refetch();
      })
      .catch((err) => {
        setDeleting(false);
        setDeleteTarget(null);
        setActionError(readableError(err));
      });
  };

  /* ---------------- filter chips ---------------- */

  const trimmedTerm = searchTerm.trim();
  const activeFilterChips = [];
  if (trimmedTerm) {
    activeFilterChips.push({ id: "search", label: `${SEARCH_LABELS[searchTarget]}: ${trimmedTerm}` });
  }
  if (ratingFilter) activeFilterChips.push({ id: "rating", label: RATING_LABELS[ratingFilter] });
  if (dateFrom || dateTo) {
    activeFilterChips.push({ id: "date", label: `Left: ${dateFrom || "-"} to ${dateTo || "-"}` });
  }

  const clearFilter = (id) => {
    if (id === "search") setSearchTerm("");
    if (id === "rating") setRatingFilter("");
    if (id === "date") {
      setDateFrom("");
      setDateTo("");
    }
  };

  const clearAllFilters = () => {
    setSearchTerm("");
    setRatingFilter("");
    setDateFrom("");
    setDateTo("");
  };

  /* ---------------- export ---------------- */

  useEffect(() => {
    if (!exporting || !exportData) return;
    const rows = [
      [config.subjectLabel, "Phone", config.authorLabel, "Phone", "Rating", "Tags", "Comment", "Left"],
    ];
    (exportData[config.root] || []).forEach((c) => {
      const subject = c[config.subjectKey];
      const author = c[config.authorKey];
      const rate = averageRate(c);
      rows.push([
        (subject && (subject.fullName || subject.name)) || "-",
        (subject && subject.phoneNumber) || "-",
        (author && (author.fullName || author.name)) || "-",
        (author && author.phoneNumber) || "-",
        rate === null ? "-" : rate.toFixed(1),
        (c.readyComments || [])
          .map((r) => r.readyComment && r.readyComment.type)
          .filter(Boolean)
          .join(", ") || "-",
        c[config.commentField] || "-",
        moment(c.createdAt).format("MM/DD/YYYY HH:mm"),
      ]);
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(rows), "Reviews");
    XLSX.writeFile(wb, `${config.title.replace(/\s+/g, "-")}.xlsx`);
    setExporting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportData]);

  const handleExport = () => {
    if (serverCount === 0 || exporting) return;
    setExporting(true);
    fetchAllForExport({ variables: { ...variables, skip: 0, limit: serverCount } });
  };

  /* ---------------- render ---------------- */

  if (loading && !data) {
    return (
      <div className="page superRidePage superCommentsPage">
        <main className="main">
          <PageHeader title={config.title} description={config.description} />
          <MetricsSkeleton count={3} />
          <TableSkeleton rows={6} columns={6} />
        </main>
      </div>
    );
  }

  if (error) {
    return <ErrorPage />;
  }

  return (
    <div className="page superRidePage superCommentsPage">
      <main className="main">
        <PageHeader
          title={config.title}
          count={serverCount}
          description={config.description}
          actions={
            <PageActionButton
              icon={<CloudDownloadIcon fontSize="small" />}
              label={exporting ? "Exporting" : "Export"}
              info={exporting ? "Preparing file" : `${serverCount} review${serverCount === 1 ? "" : "s"}`}
              onClick={handleExport}
              disabled={serverCount === 0}
              loading={exporting}
            />
          }
        />

        <section className="stats">
          <MetricCard icon={<ChatBubbleOutlineIcon />} title="Matching reviews" value={serverCount} />
          <MetricCard
            icon={<StarIcon />}
            title="Average rating"
            value={pageAverage === null ? "-" : pageAverage.toFixed(1)}
            hint="On this page"
            tone={pageAverage !== null && pageAverage < 3 ? "warning" : "default"}
          />
          <MetricCard
            icon={<ThumbDownIcon />}
            title="Below 3 stars"
            value={lowOnPage}
            hint="On this page"
            tone={lowOnPage > 0 ? "danger" : "default"}
          />
        </section>

        <section className="filters">
          <SearchField
            placeholder={`Search by ${SEARCH_LABELS[searchTarget].toLowerCase()} name or phone number...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="filtersSelects">
            <FilterDropdown
              label={`Search in: ${SEARCH_LABELS[searchTarget]}`}
              options={SEARCH_TARGETS}
              value={searchTarget}
              active={false}
              onChange={(e) => setSearchTarget(e.target.value)}
            />
            <FilterDropdown
              label={ratingFilter ? RATING_LABELS[ratingFilter] : "Rating"}
              options={RATING_OPTIONS}
              value={ratingFilter}
              onChange={(e) => setRatingFilter(e.target.value)}
            />
            <DateRangeFilter
              label="Left"
              fromValue={dateFrom}
              toValue={dateTo}
              onFromChange={(e) => setDateFrom(e.target.value)}
              onToChange={(e) => setDateTo(e.target.value)}
            />
            <FilterDropdown
              label={`Sort: ${SORT_OPTIONS.find((o) => o.value === sortOption).label}`}
              options={SORT_OPTIONS}
              value={sortOption}
              active={false}
              align="right"
              onChange={(e) => setSortOption(e.target.value)}
            />
          </div>
        </section>

        {activeFilterChips.length > 0 && (
          <ActiveFilterChipList
            filters={activeFilterChips}
            onClearFilter={clearFilter}
            onClearAll={clearAllFilters}
          />
        )}

        <div className="tableTopBar">
          <div className="tableTopBarLeft">
            {ratingFilter && (
              <span className="superRidePill">
                {comments.length} of {allComments.length} on this page
              </span>
            )}
          </div>
          <div className="tableTopBarRight">
            <IconButton
              className="tableTopIconButton"
              size="small"
              onClick={() => refetch()}
              title="Refresh reviews"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </div>
        </div>

        {actionError && (
          <div className="dsFormMessage error" role="alert">
            {actionError}
          </div>
        )}

        <div className={`tableResponsive ${loading && data ? "isRefetching" : ""}`}>
          {loading && data && <span className="superRideSpinner" aria-hidden="true" />}
          <section className="table">
            <div className="tableHeader">
              <div className="tableHeaderCell">Rating</div>
              <div className="tableHeaderCell">{config.subjectLabel}</div>
              <div className="tableHeaderCell">{config.authorLabel}</div>
              <div className="tableHeaderCell">Comment</div>
              <div className="tableHeaderCell">Left</div>
              <div className="tableHeaderCell tableHeaderAction">Actions</div>
            </div>

            {comments.length === 0 ? (
              <EmptyState
                icon={<ChatBubbleOutlineIcon />}
                title={activeFilterChips.length === 0 ? config.emptyTitle : "No matches"}
                message={
                  activeFilterChips.length === 0
                    ? "Reviews appear here once trips are completed and rated."
                    : "No reviews match these filters. Try widening or clearing them."
                }
              />
            ) : (
              comments.map((comment) => {
                const subject = comment[config.subjectKey];
                const author = comment[config.authorKey];
                const rate = averageRate(comment);
                const tags = (comment.readyComments || [])
                  .map((r) => r.readyComment && r.readyComment.type)
                  .filter(Boolean);

                return (
                  <div className="row" key={comment._id}>
                    <span data-label="Rating">
                      <Stars value={rate} />
                    </span>

                    <div className="superRidePerson" data-label={config.subjectLabel}>
                      {subject ? (
                        <React.Fragment>
                          <span className="superRidePersonName">{subject.fullName}</span>
                          <span className="superRidePersonMeta">{subject.phoneNumber}</span>
                        </React.Fragment>
                      ) : (
                        <span className="superRideMuted">Unknown</span>
                      )}
                    </div>

                    <div className="superRidePerson" data-label={config.authorLabel}>
                      {author ? (
                        <React.Fragment>
                          <span className="superRidePersonName">{author.fullName}</span>
                          <span className="superRidePersonMeta">{author.phoneNumber}</span>
                        </React.Fragment>
                      ) : (
                        <span className="superRideMuted">Unknown</span>
                      )}
                    </div>

                    <div className="superRideStack" data-label="Comment">
                      {comment[config.commentField] ? (
                        <span className="superRideComment">{comment[config.commentField]}</span>
                      ) : (
                        <span className="superRideMuted">No written comment</span>
                      )}
                      {tags.length > 0 && (
                        <span className="superRideTag">
                          {tags.length === 1 ? tags[0] : `${tags[0]} +${tags.length - 1}`}
                        </span>
                      )}
                    </div>

                    <span className="updated" data-label="Left">
                      <strong>{formatDate(comment.createdAt)}</strong>
                      <span>{formatTime(comment.createdAt)}</span>
                    </span>

                    <div className="actions">
                      <IconButton
                        size="small"
                        className="rowActionButton"
                        onClick={() => setCommentDetail(comment)}
                        title="Review details"
                      >
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        className="rowActionButton"
                        onClick={() => setDeleteTarget(comment)}
                        title="Delete review"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </div>
                  </div>
                );
              })
            )}
          </section>
        </div>

        {serverCount > 0 && (
          <PaginationComponent
            currentPage={pageCount}
            pageSize={perPage}
            totalCount={serverCount}
            pageSizeOptions={[10, 20, 50]}
            onPageChange={(newPage) => setPageCount(newPage)}
            onPageSizeChange={(size) => {
              setPerPage(parseInt(size, 10));
              setPageCount(0);
            }}
          />
        )}
      </main>

      <DialogShell
        open={Boolean(commentDetail)}
        onClose={() => setCommentDetail(null)}
        title="Review details"
      >
        {commentDetail && (
          <CommentDetails
            comment={commentDetail}
            commentField={config.commentField}
            subjectLabel={config.subjectLabel}
            authorLabel={config.authorLabel}
            subjectKey={config.subjectKey}
            authorKey={config.authorKey}
          />
        )}
      </DialogShell>

      <DialogShell
        open={Boolean(deleteTarget)}
        onClose={() => (deleting ? null : setDeleteTarget(null))}
        title="Delete review"
        size="sm"
        actions={[
          { label: "Cancel", onClick: () => setDeleteTarget(null), disabled: deleting },
          { label: "Delete", onClick: runDelete, variant: "danger", loading: deleting },
        ]}
      >
        <div className="superRideConfirm">
          This review will be removed permanently and will stop counting towards the
          {config.subjectLabel.toLowerCase()}'s rating.
        </div>
      </DialogShell>
    </div>
  );
};

export default CommentsList;
